import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../api/client'
import type {
  Availability, LiveDetail, MetricValue, Series, TargetDetail as Detail, TimeRange,
} from '../api/types'
import { Empty, ErrorBanner, Loading } from '../components/Common'
import { MetricChart } from '../components/MetricChart'
import { StatusChip } from '../components/StatusChip'
import { TimeRangePicker } from '../components/TimeRangePicker'
import { UptimeStrip } from '../components/UptimeStrip'
import { usePolling } from '../hooks/usePolling'
import { usePreference } from '../hooks/usePreferences'
import { Card } from '../components/Card'
import { formatValue, timeAgo } from '../theme/charts'

/**
 * Which charts to show for each target, and how.
 *
 * <p>Declared per target rather than derived from the metric list: a target
 * emits 60–130 series and rendering all of them would produce a wall nobody
 * reads. These are the ones that answer "is it healthy and why not".
 * Anything not listed is still available in the full metric table below.
 */
interface ChartSpec {
  title: string
  keys: string[]
  mode?: 'line' | 'area' | 'stacked-area'
  unit?: string
  /** false where every series must stay named — see MetricChart's foldSeries. */
  foldSeries?: boolean
}

const CHARTS: Record<string, ChartSpec[]> = {
  'app2-host': [
    { title: 'CPU busy', keys: ['cpu.busy_pct'], mode: 'area', unit: 'percent' },
    { title: 'Load average per CPU', keys: ['load.1m_per_cpu', 'load.5m_per_cpu', 'load.15m_per_cpu'], unit: 'percent' },
    { title: 'Memory used', keys: ['mem.used_pct'], mode: 'area', unit: 'percent' },
    { title: 'Disk used by filesystem', keys: ['disk.used_pct'], unit: 'percent' },
    { title: 'TCP established connections', keys: ['tcp.established'], mode: 'area', unit: 'count' },
    // Separate from the established count: these are small numbers that a
    // shared axis with thousands of ESTAB would flatten to zero.
    { title: 'TCP half-open (SYN-RECV)', keys: ['tcp.state_syn_recv'], mode: 'area', unit: 'count' },
    { title: 'TCP CLOSE-WAIT and TIME-WAIT', keys: ['tcp.state_close_wait', 'tcp.state_time_wait'], unit: 'count' },
  ],
  zeromq: [
    { title: 'Message rate', keys: ['zmq.messages.rate'], mode: 'area', unit: 'count/s' },
    { title: 'Last message age', keys: ['zmq.last_message_age_s'], unit: 'seconds' },
    { title: 'Operations by type', keys: ['zmq.operations.rate'], mode: 'stacked-area', unit: 'count/s' },
    { title: 'Operations by relation', keys: ['zmq.relation_ops.rate'], mode: 'stacked-area', unit: 'count/s' },
    { title: 'Handler latency', keys: ['zmq.handler_ms_avg', 'zmq.handler_ms_max'], unit: 'milliseconds' },
    { title: 'Errors per second', keys: ['zmq.json_errors.rate', 'zmq.handler_errors.rate'], unit: 'count/s' },
    { title: 'Socket connections', keys: ['zmq.host_conns', 'zmq.container_conns'], unit: 'count' },
    { title: 'Throughput', keys: ['zmq.bytes.rate'], mode: 'area', unit: 'bytes/s' },
  ],
  'app2-apache': [
    // No latency chart. Apache's Total Duration measures how long connections
    // are held, not how long requests take, so keep-alive and streaming
    // responses inflate it without anything being slow. apache.ms_per_req is
    // still collected and appears in the metric table, labelled for what it
    // actually is; real latency is on the endpoint target.
    { title: 'Capacity used', keys: ['apache.ceiling_saturation_pct'], mode: 'area', unit: 'percent' },
    { title: 'Busy and idle workers', keys: ['apache.busy_workers', 'apache.idle_workers'], unit: 'count' },
    { title: 'Workers by state', keys: ['apache.scoreboard'], mode: 'stacked-area', unit: 'count' },
    { title: 'Requests per second', keys: ['apache.accesses.rate'], mode: 'area', unit: 'count/s' },
    { title: 'Connections', keys: ['apache.conns_total', 'apache.conns_keepalive', 'apache.conns_writing'], unit: 'count' },
    { title: 'Spawned pool saturation', keys: ['apache.worker_saturation_pct'], mode: 'area', unit: 'percent' },
    { title: 'Traffic', keys: ['apache.kbytes.rate'], mode: 'area', unit: 'kilobytes/s' },
  ],
  'eurosped-db': [
    { title: 'System load', keys: ['db.sysload'], mode: 'area', unit: 'load' },
    { title: 'Connection saturation', keys: ['db.connection_saturation_pct'], mode: 'area', unit: 'percent' },
    { title: 'Connections by state', keys: ['db.connections_active', 'db.connections_idle', 'db.connections_idle_tx'], unit: 'count' },
    { title: 'Longest transaction', keys: ['db.longest_tx_s'], unit: 'seconds' },
    { title: 'Probe query time', keys: ['db.query_time_ms'], unit: 'milliseconds' },
  ],
  'tm-server': [
    { title: 'Connection rate', keys: ['tm.udp.total_connections.rate', 'tm.tcp.total_connections.rate'], unit: 'count/s' },
    { title: 'Failure ratio', keys: ['tm.udp.failure_ratio_pct', 'tm.tcp.failure_ratio_pct'], unit: 'percent' },
    { title: 'Storage queue depth', keys: ['tm.storage.queue_waiting'], mode: 'area', unit: 'count' },
    { title: 'Average DB insert time', keys: ['tm.storage.avg_insert_ms'], unit: 'milliseconds' },
    { title: 'DB pool saturation', keys: ['tm.db_pool.saturation_pct'], mode: 'area', unit: 'percent' },
    { title: 'Cached clients', keys: ['tm.clients.cached'], mode: 'area', unit: 'count' },
    { title: 'Clients by TM version', keys: ['tm.clients_by_version'], mode: 'stacked-area', unit: 'count' },
    { title: 'Clients by installation type', keys: ['tm.clients_by_install_type'], mode: 'stacked-area', unit: 'count' },
    { title: 'ZMQ published messages', keys: ['tm.zmq.published_messages.rate'], unit: 'count/s' },
    { title: 'Thread count', keys: ['tm.threads.total'], unit: 'count' },
  ],
  'tm-import': [
    { title: 'Failing services', keys: ['import.services_failing_pct'], mode: 'area', unit: 'percent' },
    // Distinct companies, so the legend and the numbers agree with the title —
    // 55 companies behind 61 active services. The per-service equivalents stay
    // on the metrics table under their own names.
    { title: 'Imported companies', keys: ['import.companies_active', 'import.companies_failing', 'import.companies_suspended'], unit: 'count' },
    // Data freshness, which is a different question from importer liveness —
    // every run can succeed while the positions coming back are hours old.
    { title: 'Position age (median)', keys: ['import.position_age_median_s'], mode: 'area', unit: 'seconds' },
    // foldSeries: false on all three — there are 18 import types and folding
    // the tail into "Other" would hide the very provider being looked for.
    { title: 'Position age by provider', keys: ['import.type_position_age_median_s'], unit: 'seconds', foldSeries: false },
    { title: 'Failing companies by provider', keys: ['import.type_services_failing'], mode: 'stacked-area', unit: 'count', foldSeries: false },
    { title: 'Failing cars by provider', keys: ['import.type_cars_failing'], mode: 'stacked-area', unit: 'count', foldSeries: false },
    { title: 'Cars in service by provider', keys: ['import.type_cars_live'], unit: 'count', foldSeries: false },
    { title: 'Cars', keys: ['import.cars_live', 'import.cars_unlinked', 'import.cars_failing'], unit: 'count' },
    { title: 'Oldest service run', keys: ['import.oldest_run_age_s'], unit: 'seconds' },
  ],
  'app2-endpoint': [
    { title: 'TLS days to expiry', keys: ['tls.days_to_expiry'], unit: 'days' },
  ],
  tmapi: [
    { title: 'Response time', keys: ['http.response_ms'], mode: 'line', unit: 'milliseconds' },
    { title: 'Response size', keys: ['http.response_bytes'], unit: 'bytes' },
  ],
  tomcat: [
    { title: 'Thread pool saturation', keys: ['tomcat.thread_saturation_pct'], mode: 'area', unit: 'percent' },
    { title: 'Threads', keys: ['tomcat.threads_busy', 'tomcat.threads_current'], unit: 'count' },
    { title: 'Request rate', keys: ['tomcat.requests.rate'], mode: 'area', unit: 'count/s' },
    { title: 'Response time (current interval)', keys: ['tomcat.ms_per_request'], unit: 'milliseconds' },
    // Kept on its own chart rather than beside the request rate: the two share
    // no scale, and the note explaining what Tomcat counts as an error needs
    // to sit with the number it qualifies.
    { title: 'Error ratio', keys: ['tomcat.error_ratio_pct'], mode: 'line', unit: 'percent' },
    { title: 'Errors per second', keys: ['tomcat.errors.rate'], unit: 'count/s' },
    { title: 'Heap used', keys: ['jvm.heap_used_pct'], mode: 'area', unit: 'percent' },
    { title: 'Memory pools', keys: ['jvm.pool_used'], mode: 'stacked-area', unit: 'bytes' },
    { title: 'Traffic out', keys: ['tomcat.bytes_out.rate'], mode: 'area', unit: 'bytes/s' },
  ],
}

export function TargetDetailPage() {
  const { id = '' } = useParams()
  // Remembered per user rather than per page: someone who works in weekly
  // ranges wants weekly everywhere, and re-picking it on every target is a
  // papercut that shows up dozens of times a day.
  const [range, setRange] = usePreference<TimeRange>('range.targetDetail', 'day')

  const detail = usePolling<Detail>(() => api.target(id), 20_000, [id])
  const availability = usePolling<Availability>(
    () => api.availability(id, range === 'hour' ? 'day' : range), 60_000, [id, range])

  if (detail.loading && !detail.data) return <Loading what="target" />
  if (detail.error && !detail.data) return <ErrorBanner error={detail.error} />
  if (!detail.data) return <Empty>No such target.</Empty>

  const { target, status, metrics } = detail.data
  const specs = CHARTS[id] ?? []

  return (
    <div className="stack">
      <div>
        <div className="spread">
          <div>
            <h1 className="page-title">{target.name}</h1>
            <p className="page-subtitle">
              {target.descriptor ?? target.probeType}
              {' · '}{target.criticality.toLowerCase()}
              {' · every '}{target.intervalSeconds}s
              {status && ` · probed ${timeAgo(status.lastProbeAt)}`}
            </p>
          </div>
          {status && <StatusChip status={status.status} />}
        </div>

        {status && !status.reachable && status.lastError && (
          <div className="banner error">
            <strong>Unreachable.</strong> {status.lastError}
          </div>
        )}

        {status && status.reasons.length > 0 && (
          <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
            <div className="card-header"><h3 className="card-title">Why it is not healthy</h3></div>
            <div className="card-body" style={{ paddingTop: 0 }}>
              <table className="table">
                <tbody>
                  {status.reasons.map((r) => (
                    <tr key={r.ruleId + (r.label ?? '')}>
                      <td style={{ width: 100 }}><StatusChip status={r.severity} /></td>
                      <td>{r.summary}</td>
                      <td className="key">{r.ruleId}</td>
                      <td className="muted num">{timeAgo(r.since)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Availability sits above the range picker: it answers "has this been
          up?" and is the first thing looked at, while the picker below governs
          the charts that follow it. */}
      <Card
        title="Availability"
        prefKey={`target.${id}.availability`}
        meta={availability.data
          ? `last ${range === 'hour' ? 'day' : range}`
          : undefined}
      >
        {availability.data
          ? <UptimeStrip availability={availability.data} />
          : <Loading what="availability" />}
      </Card>

      <div className="toolbar">
        <TimeRangePicker value={range} onChange={setRange} />
        <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
          Charts pick the storage tier that fits the range; each one says which it used.
        </span>
      </div>

      {specs.length > 0 ? (
        <div className="grid grid-2">
          {specs.map((spec) => (
            <ChartCard key={spec.title} targetId={id} spec={spec} range={range} />
          ))}
        </div>
      ) : (
        <Empty>
          No charts are configured for this target yet. Every collected metric is
          still listed below.
        </Empty>
      )}

      {id === 'tm-server' && <LiveDetailPanel targetId={id} />}

      <CurrentMetricsTable metrics={metrics} />
    </div>
  )
}

function ChartCard({ targetId, spec, range }: {
  targetId: string
  spec: ChartSpec
  range: TimeRange
}) {
  const [series, setSeries] = useState<Series[]>([])
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.series(targetId, spec.keys, range)
      .then((data) => { if (!cancelled) { setSeries(data); setError(null) } })
      .catch((e) => { if (!cancelled) setError(e) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [targetId, range, spec.keys.join(',')])

  if (error) {
    return <Card title={spec.title}><ErrorBanner error={error} /></Card>
  }
  if (loading && series.length === 0) {
    return <Card title={spec.title}><Loading what={spec.title} /></Card>
  }

  return (
    <MetricChart
      title={spec.title}
      series={series}
      range={range}
      mode={spec.mode}
      unit={spec.unit}
      foldSeries={spec.foldSeries}
      prefKey={`chart.${targetId}.${spec.keys[0]}`}
    />
  )
}

/**
 * Live, unstored detail pulled straight from the target.
 *
 * <p>Verbose is ~126 KB and contains a row per connected client, so it is
 * fetched only when asked for and never written to the database.
 */
function LiveDetailPanel({ targetId }: { targetId: string }) {
  const [mode, setMode] = useState('normal')
  const [detail, setDetail] = useState<LiveDetail | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function fetchNow(which: string) {
    setBusy(true)
    setError(null)
    setMode(which)
    try {
      setDetail(await api.live(targetId, which))
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="Live detail"
      prefKey={`target.${targetId}.live`}
      defaultCollapsed
      meta="fetched on demand · never stored"
      note="Pulled straight from the server when you ask for it and never written to the database. Verbose includes a row per connected client, so it is deliberately not collected on a schedule."
    >
      <>
        <div className="toolbar">
          {['basic', 'normal', 'verbose'].map((m) => (
            <button key={m} type="button" disabled={busy} onClick={() => fetchNow(m)}>
              {busy && mode === m ? 'Fetching…' : m}
            </button>
          ))}
          {detail && (
            <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
              {detail.mode} · {(detail.bytes / 1024).toFixed(1)} KB
              {' · '}{detail.durationMs} ms
              {' · '}{new Date(detail.fetchedAt).toLocaleTimeString()}
            </span>
          )}
        </div>
        <ErrorBanner error={error} />
        {detail
          ? <pre className="pre">{detail.content}</pre>
          : <Empty>Pick a verbosity level to pull the current server report.</Empty>}
      </>
    </Card>
  )
}

function CurrentMetricsTable({ metrics }: { metrics: MetricValue[] }) {
  const [filter, setFilter] = useState('')

  const rows = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    const filtered = needle
      ? metrics.filter((m) =>
          m.key.toLowerCase().includes(needle)
          || (m.label ?? '').toLowerCase().includes(needle)
          || (m.displayName ?? '').toLowerCase().includes(needle))
      : metrics
    return [...filtered].sort((a, b) =>
      a.key.localeCompare(b.key) || a.label.localeCompare(b.label))
  }, [metrics, filter])

  return (
    <Card
      title="All current values"
      prefKey="target.metricsTable"
      meta={`${rows.length} of ${metrics.length} series`}
    >
      <>
        <input
          placeholder="Filter metrics…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ width: '100%', marginBottom: 'var(--space-3)' }}
        />
        <div style={{ maxHeight: 460, overflow: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Label</th>
                <th className="num">Value</th>
                <th className="num">Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.key + '|' + m.label}>
                  <td>
                    <div>{m.displayName ?? m.key}</div>
                    <div className="key">{m.key}</div>
                    {m.description && (
                      <div className="metric-note-inline" title={m.description}>
                        {m.description}
                      </div>
                    )}
                  </td>
                  <td className="mono">{m.label || '—'}</td>
                  <td className="num">{formatValue(m.value, m.unit)}</td>
                  <td className="num muted">{timeAgo(m.time)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    </Card>
  )
}
